import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import ScheduledEventCard from "./ScheduledEventCard";
import LaunchedTemplateReviewForm from "../../components/LaunchedTemplates/LaunchedTemplateReviewForm";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import { toast } from "react-toastify";

// Common APIs (shared with Automation):
// Modules    -> GET /module-plans/module-plans/default-db
// Categories -> GET /automation-category/module/{moduleId}/default-db
// (Main components load from the Default Tenant DB via the /default-db endpoints)
//
// Notification-specific:
// Event Types + Scheduled Event Templates (top 10 per type + count)
//   -> GET/scheduled-events/scheduled-events/category/{categoryId}
// View More (all scheduled events for an Event Type)
//   -> GET/scheduled-events/scheduled-events/event-type/{eventTypeId}

const getTemplateCode = (template) =>
  template?.templateCode ??
  template?.notification?.templateCode ??
  template?.automation?.templateCode;

const NotificationMain = ({ displayMode = "gallery" }) => {
  const { canAccessModule } = useAuth();
  const navigate = useNavigate();
  // Deep link from the Explore tab's "View used template" button:
  // ?moduleId=&categoryId=&eventTypeId=&templateCode=
  // Opens that module/category and highlights the copy with that templateCode.
  const [searchParams] = useSearchParams();
  // URL params first; fall back to what Explore stashed in sessionStorage
  // (survives a route redirect that drops the query string). Read once.
  const [deepLink] = useState(() => {
    const fromUrl = {
      moduleAccessId: searchParams.get("moduleAccessId"),
      categoryId: searchParams.get("categoryId"),
      typeId: searchParams.get("eventTypeId"),
      templateCode: searchParams.get("templateCode"),
      scheduledIds: (searchParams.get("scheduledIds") || "")
        .split(",")
        .filter(Boolean),
    };
    if (fromUrl.moduleAccessId || fromUrl.templateCode) return fromUrl;
    try {
      const saved = JSON.parse(
        sessionStorage.getItem("usedTemplateDeepLink") || "null",
      );
      if (saved && Date.now() - saved.ts < 60000) {
        return { ...saved, scheduledIds: saved.scheduledIds || [] };
      }
    } catch {
      /* ignore */
    }
    return { scheduledIds: [] };
  });
  const targetModuleAccessId = deepLink.moduleAccessId;
  const targetCategoryId = deepLink.categoryId;
  const targetTypeId = deepLink.typeId;
  const highlightCode = deepLink.templateCode;
  const highlightIds = deepLink.scheduledIds || [];
  const hasDeepLink = Boolean(
    targetModuleAccessId || highlightCode || highlightIds.length,
  );
  // Prefer the exact copy IDs; fall back to the shared templateCode.
  const isHighlighted = (item) =>
    highlightIds.length > 0
      ? highlightIds.includes(String(item.scheduledEventId))
      : Boolean(highlightCode) &&
        String(getTemplateCode(item)) === String(highlightCode);
  const deepLinkAppliedRef = useRef(false);
  const deepLinkScrolledRef = useRef(false);
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoadingModules, setIsLoadingModules] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const [eventTypes, setEventTypes] = useState([]);
  const [isLoadingEventTypes, setIsLoadingEventTypes] = useState(false);

  const [error, setError] = useState(null);
  const [reviewItem, setReviewItem] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const selectedModuleHasAccess = selectedModule
    ? canAccessModule(
        selectedModule.moduleAccessId ?? selectedModule.moduleAccessID,
      )
    : false;

  const toggleScheduledEvent = async (event) => {
    const id = event.scheduledEventId;
    if (!id) return;
    const enable = String(event.status).toUpperCase() !== "ENABLED";
    try {
      setTogglingId(id);
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/scheduled-events/${enable ? "enable" : "disable"}/${id}`,
      );
      setEventTypes((types) =>
        types.map((type) => ({
          ...type,
          scheduledEvents: (type.scheduledEvents || []).map((item) =>
            item.scheduledEventId === id
              ? { ...item, status: enable ? "ENABLED" : "DISABLED" }
              : item,
          ),
        })),
      );
    } catch (err) {
      console.error("Failed to update notification status", err);
      setError("Unable to update notification status.");
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDeleteScheduledEvent = async () => {
    const id = deleteItem?.scheduledEventId;
    if (!id || deletingId) return;

    try {
      setDeletingId(id);
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/scheduled-events/${id}`,
      );
      toast.success("Notification template deleted successfully.");
      setDeleteItem(null);
      if (selectedCategory) await handleCategoryClick(selectedCategory);
    } catch (err) {
      console.error("Failed to delete notification template", err);
      setError("Unable to delete notification template.");
      toast.error("Unable to delete notification template.");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setIsLoadingModules(true);
        setError(null);
        const { data } = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/module-plans/module-plans`,
        );
        setModules(Array.isArray(data) ? data : data?.data || []);
      } catch (err) {
        console.error("Failed to fetch modules", err);
        setError("Failed to load modules.");
        setModules([]);
      } finally {
        setIsLoadingModules(false);
      }
    };

    fetchModules();
  }, []);

  const handleModuleClick = async (mod) => {
    const moduleId = mod.moduleId ?? mod.id;

    setSelectedModule(mod);
    setSelectedCategory(null);
    setCategories([]);
    setEventTypes([]);

    try {
      setIsLoadingCategories(true);
      setError(null);
      const { data } = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/automation-category/module/${moduleId}`,
      );
      setCategories(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      console.error("Failed to fetch categories", err);
      setError("Failed to load categories.");
      setCategories([]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleCategoryClick = async (category) => {
    const categoryId = category.categoryId ?? category.id;

    setSelectedCategory(category);
    setEventTypes([]);

    try {
      setIsLoadingEventTypes(true);
      setError(null);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/scheduled-events/scheduled-events/category/${categoryId}`,
      );
      // Response shape: { status, message, data: { categoryId, categoryName, eventTypes: [...] } }
      const payload = response?.data?.data ?? response?.data ?? {};
      setEventTypes(payload.eventTypes || []);
    } catch (err) {
      console.error("Failed to fetch event types", err);
      setError("Failed to load event types.");
      setEventTypes([]);
    } finally {
      setIsLoadingEventTypes(false);
    }
  };

  const handleViewMore = (eventType) => {
    // TODO: next step - fetch all scheduled events for this Event Type
    // GET/scheduled-events/scheduled-events/event-type/{eventTypeId}
    console.log("View More clicked for event type:", eventType);
  };

  const createTemplate = (eventType) => {
    setReviewItem({
      scheduledEventId: null,
      eventTypeId: eventType.eventTypeId,
      taskName: "",
      notificationTypes: [],
      toEmailSourceFrom: "",
      fromEmailSource: "",
      fromEmail: "",
      eventTemplate: {},
      alreadyUsed: false,
    });
  };

  // Navigate to the Explore tab with this module/category/event-type
  // pre-selected via query params, so the person lands straight on the
  // matching filtered view instead of the "browse everything" default.
  const exploreType = (eventType) => {
    const params = new URLSearchParams();
    const moduleId = selectedModule?.moduleId ?? selectedModule?.id;
    const categoryId = selectedCategory?.categoryId ?? selectedCategory?.id;
    const eventTypeId = eventType.eventTypeId;

    if (moduleId) params.set("moduleId", moduleId);
    if (categoryId) params.set("categoryId", categoryId);
    if (eventTypeId) params.set("eventTypeId", eventTypeId);

    navigate(`/admin/notification/explore?${params.toString()}`);
  };

  // Open the first available module/category so the page is useful on entry.
  useEffect(() => {
    const targetModule = targetModuleAccessId
      ? modules.find(
          (module) =>
            String(module.moduleAccessId ?? module.moduleAccessID) ===
            String(targetModuleAccessId),
        )
      : null;
    const firstModule =
      targetModule ??
      modules.find((module) =>
        canAccessModule(module.moduleAccessId ?? module.moduleAccessID),
      ) ??
      modules[0];
    if (!isLoadingModules && firstModule && !selectedModule) {
      handleModuleClick(firstModule);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingModules, modules, selectedModule, canAccessModule]);

  useEffect(() => {
    if (
      selectedModule &&
      selectedModuleHasAccess &&
      !isLoadingCategories &&
      categories.length > 0 &&
      !selectedCategory
    ) {
      const targetCategory =
        !deepLinkAppliedRef.current &&
        targetCategoryId &&
        String(
          selectedModule.moduleAccessId ?? selectedModule.moduleAccessID,
        ) === String(targetModuleAccessId)
          ? categories.find(
              (cat) =>
                String(cat.categoryId ?? cat.id) === String(targetCategoryId),
            )
          : null;
      deepLinkAppliedRef.current = true;
      handleCategoryClick(targetCategory ?? categories[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedModule,
    selectedModuleHasAccess,
    isLoadingCategories,
    categories,
    selectedCategory,
  ]);

  // Once the deep-linked category has loaded, scroll to the highlighted
  // template (or, failing that, its type) a single time.
  useEffect(() => {
    if (deepLinkScrolledRef.current) return;
    if (!hasDeepLink) return;
    if (isLoadingEventTypes || eventTypes.length === 0) return;
    if (
      targetCategoryId &&
      String(selectedCategory?.categoryId ?? selectedCategory?.id) !==
        String(targetCategoryId)
    )
      return;

    deepLinkScrolledRef.current = true;
    try {
      sessionStorage.removeItem("usedTemplateDeepLink");
    } catch {
      /* ignore */
    }
    requestAnimationFrame(() => {
      const target =
        document.querySelector(".scheduled-event-card--highlighted") ??
        document.getElementById(`event-type-${targetTypeId}`);
      if (target)
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      if (
        (highlightCode || highlightIds.length) &&
        !document.querySelector(".scheduled-event-card--highlighted")
      ) {
        toast.info("We couldn't find that template in your list.");
      }
    });
  }, [
    isLoadingEventTypes,
    eventTypes,
    selectedCategory,
    highlightCode,
    highlightIds.length,
    hasDeepLink,
    targetTypeId,
    targetCategoryId,
  ]);

  return (
    <div className="notification-main">
      {/* MODULE MENU */}
      <div className="custom-menu">
        {isLoadingModules ? (
          <div className="custom-menu-item">Loading modules...</div>
        ) : modules.length === 0 ? (
          <div className="custom-menu-item">No modules found.</div>
        ) : (
          modules.map((mod) => {
            const moduleId = mod.moduleId ?? mod.id;
            const isActive =
              (selectedModule?.moduleId ?? selectedModule?.id) === moduleId;
            const hasModuleAccess = canAccessModule(
              mod.moduleAccessId ?? mod.moduleAccessID,
            );

            return (
              <div
                key={moduleId}
                className={`custom-menu-item ${
                  isActive ? "custom-active" : ""
                } ${hasModuleAccess ? "" : "custom-menu-item--locked"}`}
                onClick={() => handleModuleClick(mod)}
                title={
                  hasModuleAccess
                    ? undefined
                    : "You have not purchased this module."
                }
              >
                {mod.moduleName ?? mod.name}
                {!hasModuleAccess && <Icon icon="mdi:lock-outline" />}
              </div>
            );
          })
        )}
      </div>

      {/* CATEGORY INNER MENU + CONTENT */}
      <div className="custom-content">
        {error && <div className="custom-error">{error}</div>}

        {selectedModule && (
          <div className="custom-inner-menu">
            {isLoadingCategories ? (
              <div className="custom-inner-menu-item">
                Loading categories...
              </div>
            ) : categories.length === 0 ? (
              <div className="custom-inner-menu-item">
                No categories found for this module.
              </div>
            ) : (
              categories.map((cat) => {
                const categoryId = cat.categoryId ?? cat.id;
                const isActive =
                  (selectedCategory?.categoryId ?? selectedCategory?.id) ===
                  categoryId;

                return (
                  <div
                    key={categoryId}
                    className={`custom-inner-menu-item ${
                      isActive ? "custom-active" : ""
                    } ${
                      selectedModuleHasAccess
                        ? ""
                        : "custom-inner-menu-item--locked"
                    }`}
                    onClick={() => handleCategoryClick(cat)}
                    title={
                      selectedModuleHasAccess
                        ? undefined
                        : "Locked — purchase this module to view"
                    }
                  >
                    {cat.categoryName ?? cat.name}
                    {!selectedModuleHasAccess && (
                      <Icon icon="mdi:lock-outline" />
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {!selectedModule && !isLoadingModules && !error && (
          <div className="empty-state empty-state--inline">
            <p>Select a module to manage its notification categories.</p>
          </div>
        )}

        {selectedModule && !selectedModuleHasAccess && (
          <div className="empty-state empty-state--inline module-locked-banner">
            <Icon icon="mdi:lock-outline" />
            <p>
              You haven't purchased the "
              {selectedModule.moduleName ?? selectedModule.name}" module.
            </p>
            <button
              type="button"
              onClick={() => window.location.assign("https://ourdept.com")}
            >
              View plans
            </button>
          </div>
        )}

        {/* EVENT TYPES + SCHEDULED EVENT TEMPLATES */}
        {selectedCategory && selectedModuleHasAccess && (
          <div className="notification-main__event-types">
            {isLoadingEventTypes ? (
              <div>Loading event types...</div>
            ) : eventTypes.length === 0 ? (
              <div>No event types found for this category.</div>
            ) : (
              eventTypes.map((eventType) => {
                const scheduledEvents = eventType.scheduledEvents || [];
                const totalCount = eventType.count ?? scheduledEvents.length;

                return (
                  <div
                    key={eventType.eventTypeId}
                    id={`event-type-${eventType.eventTypeId}`}
                    className="event-type-card"
                  >
                    <div className="event-type-card__header">
                      <h3>{eventType.eventTypeName}</h3>
                      <div className="downloaded-template-actions">
                        <span className="event-type-card__count">
                          {scheduledEvents.length} template
                          {scheduledEvents.length === 1 ? "" : "s"}
                        </span>
                        <button
                          type="button"
                          className="explore-type-button"
                          onClick={() => exploreType(eventType)}
                        >
                          <Icon icon="mdi:creation" /> Explore
                        </button>
                        <button
                          type="button"
                          onClick={() => createTemplate(eventType)}
                        >
                          <Icon icon="mdi:plus" /> New
                        </button>
                      </div>
                    </div>

                    {scheduledEvents.length === 0 ? (
                      <p className="event-type-card__empty">
                        No scheduled event templates for this event type.
                      </p>
                    ) : (
                      <div
                        className={`scheduled-event-grid scheduled-event-grid--${displayMode}`}
                      >
                        {scheduledEvents.map((event) => (
                          <ScheduledEventCard
                            key={event.scheduledEventId}
                            event={event}
                            highlighted={isHighlighted(event)}
                            // These records already belong to this tenant. The
                            // review form uses `alreadyUsed` to decide whether
                            // to include scheduledEventId in its save payload.
                            onReview={(item) =>
                              setReviewItem({ ...item, alreadyUsed: true })
                            }
                            onToggleStatus={toggleScheduledEvent}
                            onDelete={setDeleteItem}
                            isTogglingStatus={
                              togglingId === event.scheduledEventId
                            }
                            isDeleting={deletingId === event.scheduledEventId}
                            reviewIconOnly
                            displayMode={displayMode}
                          />
                        ))}
                      </div>
                    )}

                    {totalCount > scheduledEvents.length && (
                      <button
                        type="button"
                        className="view-more-button"
                        onClick={() => handleViewMore(eventType)}
                      >
                        View More
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {reviewItem && (
        <LaunchedTemplateReviewForm
          item={reviewItem}
          type="event"
          onClose={() => setReviewItem(null)}
          onSaved={() => {
            setReviewItem(null);
            if (selectedCategory) handleCategoryClick(selectedCategory);
          }}
        />
      )}
      <WarningPopup
        isOpen={Boolean(deleteItem)}
        onClose={() => !deletingId && setDeleteItem(null)}
        onDecline={() => !deletingId && setDeleteItem(null)}
        onAccept={confirmDeleteScheduledEvent}
        title="Delete notification template?"
        message="This action permanently deletes the template and cannot be retrieved. Do you want to continue?"
        acceptText={deletingId ? "Deleting..." : "Yes, delete"}
        declineText="Cancel"
      />
    </div>
  );
};

export default NotificationMain;
